import { useCallback, useMemo } from "react"
import { Account } from "@mezo/api"
import { PayloadAction } from "@reduxjs/toolkit"

import {
  loginAccount,
  resetOnboardingFlow,
  selectOnboardingStep,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { useWalletDisconnect } from "#/shared/hooks"
import { usePosthogSignUpEvent } from "#/shared/hooks/posthog-events"
import { OnboardingStep } from "#/shared/types"

import BaseModal, { BaseModalProps } from "../BaseModal"

import OnboardingModalBody from "./OnboardingModalBody"

export default function OnboardingModal({ isOpen, onClose }: BaseModalProps) {
  const { emitSignUpCompletedEvent } = usePosthogSignUpEvent()
  const { disconnect } = useWalletDisconnect()

  const onboardingStep = useDappSelector(selectOnboardingStep)
  const dispatch = useDappDispatch()

  const isLoadingStateModal = useMemo(
    () =>
      onboardingStep === OnboardingStep.ACCOUNT_CREATION_IN_PROGRESS ||
      onboardingStep === OnboardingStep.CLAIMING_REWARDS_IN_PROGRESS,
    [onboardingStep],
  )

  const modalCloseHandler = useCallback(async () => {
    // Prevent users from closing the modal while the state is in progress
    if (!isLoadingStateModal) {
      onClose?.({})
      dispatch(resetOnboardingFlow())
    }

    // Disconnect user if modal is closed before account is created
    if (onboardingStep <= OnboardingStep.CREATE_ACCOUNT) {
      disconnect()
    }

    // Fetch account data when onboarding is completed
    if (onboardingStep === OnboardingStep.ACCOUNT_CREATED) {
      const { payload: account } = (await dispatch(
        loginAccount(),
      )) as PayloadAction<Account>

      if (!account) return

      emitSignUpCompletedEvent(account.address, account.username)
    }
  }, [
    onClose,
    dispatch,
    onboardingStep,
    isLoadingStateModal,
    disconnect,
    emitSignUpCompletedEvent,
  ])

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={modalCloseHandler}
      closeable={!isLoadingStateModal}
    >
      <OnboardingModalBody closeModal={modalCloseHandler} />
    </BaseModal>
  )
}
